<template>
  <div class="flex flex-col gap-6">
    <UnitFormSection title="Endereço">
      <div class="md:col-span-3 relative">
        <GenericInput
          :disabled="disabled || isLoadingCep"
          required
          label="CEP"
          :rules="[rules.form('postalCode')]"
          width="100%"
          ref="postalCodeField"
          :model-value="form.values.postalCode"
          @update:model-value="onPostalCodeInput"
          @validate="validate('postalCode')"
          @input="emit('address-changed')"
          :loading="isLoadingCep"
          validate-on="submit"
          data-testid="unit-cep-input"
        />
        <div v-if="isLoadingCep" class="absolute right-0 top-0 mt-4 mr-2">
          <v-progress-circular indeterminate color="primary" size="20"></v-progress-circular>
        </div>
        <!-- Aviso, não erro: a busca do CEP falhou mas os campos seguem editáveis. -->
        <p
          v-if="cepWarning"
          role="status"
          class="mt-1 flex items-start gap-1 text-xs text-left text-amber-700"
          data-testid="unit-cep-lookup-warning"
        >
          <v-icon :icon="mdiAlertOutline" size="14" class="mt-px shrink-0" />
          <span>{{ cepWarning }}</span>
        </p>
      </div>

      <div class="md:col-span-3">
        <v-autocomplete
          :disabled="disabled"
          v-model="form.values.state"
          class="w-full"
          :rules="[rules.required]"
          required
          color="primary20"
          label="* Estado"
          theme="light"
          :items="StatesFromBR"
          variant="outlined"
          data-testid="unit-state-select"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          required
          label="Cidade"
          :rules="[rules.form('city')]"
          width="100%"
          v-model="form.values.city"
          @validate="validate('city')"
          @input="emit('address-changed')"
          data-testid="unit-city-input"
        />
      </div>

      <div class="md:col-span-8">
        <GenericInput
          :disabled="disabled"
          required
          label="Rua"
          :rules="[rules.form('street')]"
          width="100%"
          v-model="form.values.street"
          @validate="validate('street')"
          @input="emit('address-changed')"
          data-testid="unit-street-input"
        />
      </div>

      <div class="md:col-span-4">
        <GenericInput
          :disabled="disabled"
          required
          label="Número"
          :rules="[rules.form('number')]"
          width="100%"
          v-model="form.values.number"
          @validate="validate('number')"
          @input="emit('address-changed')"
          data-testid="unit-number-input"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          required
          label="Bairro"
          :rules="[rules.form('neighborhood')]"
          width="100%"
          v-model="form.values.neighborhood"
          @validate="validate('neighborhood')"
          @input="emit('address-changed')"
          data-testid="unit-neighborhood-input"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          label="Complemento"
          :rules="[rules.form('complement')]"
          width="100%"
          v-model="form.values.complement"
          @validate="validate('complement')"
          data-testid="unit-complement-input"
        />
      </div>
    </UnitFormSection>

    <UnitFormSection title="Coordenadas" hint="preenchidas ao verificar a localização">
      <div class="md:col-span-6">
        <GenericInput
          :rules="[rules.form('latitude')]"
          @validate="validate('latitude')"
          label="Latitude"
          width="100%"
          disabled
          :model-value="latitude"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :rules="[rules.form('longitude')]"
          @validate="validate('longitude')"
          label="Longitude"
          width="100%"
          disabled
          :model-value="longitude"
        />
      </div>

      <div class="md:col-span-12 mt-1">
        <UnitLocationStatus
          :confirmed="locationConfirmed"
          :verifying="isVerifyingLocation"
          :readonly="disabled"
          @verify="emit('verify-location')"
        />
      </div>
    </UnitFormSection>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiAlertOutline } from '@mdi/js'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import StatesFromBR from '@/config/StatesFromBR'
import { injectHealthUnitForm } from '../../composables/useHealthUnitForm'
import { maskPostalCodeInput } from '../../helpers/postalCode'
import { syncMaskedInput } from '../../helpers/maskedInput'
import type { MaskedField } from '../../helpers/maskedInput'
import UnitFormSection from './UnitFormSection.vue'
import UnitLocationStatus from './UnitLocationStatus.vue'

withDefaults(
  defineProps<{
    disabled: boolean
    isLoadingCep: boolean
    /** Motivo da falha na busca do CEP — exibido como aviso, sem bloquear o formulário. */
    cepWarning?: string | null
    latitude: string | null
    longitude: string | null
    locationConfirmed: boolean
    isVerifyingLocation: boolean
  }>(),
  { cepWarning: null }
)

const emit = defineEmits<{
  (e: 'address-changed'): void
  (e: 'verify-location'): void
}>()

const { form, validate, rules } = injectHealthUnitForm()

const postalCodeField = ref<MaskedField>(null)

function onPostalCodeInput(value: string | number | undefined): void {
  const masked = maskPostalCodeInput(value)
  form.values.postalCode = masked
  syncMaskedInput(postalCodeField, masked)
}
</script>
